// src/shared/useContinueLearning.ts
// ─────────────────────────────────────────────────────────────────────────
// Binds resolveContinueLearningTarget to live local progress + real module
// data so the nav consumes one ready-to-render value. Recomputes whenever
// progress changes (useProgress bumps `version`, which also fires on
// assessment writes, so a passed final test moves the recommendation
// immediately).
//
// The full LEARNING_MODULES list is passed (so #1 to #3 can resume school
// lessons via last-active); the resolver itself restricts the ordered
// recommendation scan (#4 and #5) to Core Path modules. positionModuleId
// carries the next-up module from the completion model in
// corePathPosition.ts.
// ─────────────────────────────────────────────────────────────────────────

import { useMemo } from 'react';
import { LEARNING_MODULES } from '@nisk/core/learning';
import { useProgress } from './useProgress';
import { getCorePathPosition } from './corePathPosition';
import {
	resolveContinueLearningTarget,
	readLastActive,
	type ContinueTarget,
} from './continueLearning';

export function useContinueLearning(): ContinueTarget {
	const { version, isLessonComplete } = useProgress();

	return useMemo(() => {
		const last = readLastActive();
		const position = getCorePathPosition();
		return resolveContinueLearningTarget({
			modules: LEARNING_MODULES,
			isComplete: isLessonComplete,
			lastActiveModuleId: last?.moduleId ?? null,
			lastActiveLessonId: last?.lessonId ?? null,
			positionModuleId: position.positionModule?.id ?? null,
		});
		// version is the recompute trigger; isLessonComplete is stable per version
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [version]);
}
